import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

/**
 * A "load more" list backed by a page-based GraphQL query.
 *
 * Rows accumulate instead of replacing each other, so a broker can scroll the
 * whole meeting list in one view rather than paging through it mid-presentation.
 * `resetKey` collapses back to page one whenever the filters that define the
 * list change.
 *
 * `field` is the query's root field and `rowsKey` the array inside it — passed
 * as names rather than a selector function so the effect below can depend on
 * the rows themselves, which stay referentially stable between renders.
 */
const usePagedRows = ({
  query,
  variables,
  field,
  rowsKey,
  pageSize,
  resetKey,
}) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const { data, loading, error, refetch } = useQuery(query, {
    variables: { ...variables, pagination: { page, limit: pageSize } },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const payload = data?.[field];
  const fresh = payload?.[rowsKey];
  const totalCount = payload?.totalCount ?? 0;

  useEffect(() => {
    if (!fresh) return;

    setRows((prev) => {
      if (page === 1) return fresh;
      // De-duplicated because a page can land after the filters have already
      // reset the list; re-appending page one would otherwise double the view.
      const seen = new Set(prev.map((r) => r._id));
      return [...prev, ...fresh.filter((r) => !seen.has(r._id))];
    });
  }, [fresh, page]);

  return {
    rows,
    totalCount,
    error,
    loading,
    refetch,
    // True only while the very first page is in flight — later pages keep the
    // rows on screen and put the spinner on the button instead.
    firstLoad: loading && page === 1 && rows.length === 0,
    hasMore: rows.length < totalCount,
    remaining: Math.max(totalCount - rows.length, 0),
    loadMore: () => setPage((p) => p + 1),
  };
};

export default usePagedRows;
